import {
  Box,
  Button,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
} from '@mui/material';
import ClientList from '../filters/clients/ClientList';
import TpaMultiSelectFilter from '../filters/tpa/TpaMultiSelectFilter';
import TpaList from '../filters/tpa/TPAList';
import { FORM_LABELS, REPORT_TYPES, BUTTONS } from '../constants';
import { useReportForm } from './ReportFormContext';

export const StandardReportForm = () => {
  const {
    reportType,
    handleReportTypeChange,
    startDate,
    handleStartDateChange,
    isStartDateInvalid,
    endDate,
    handleEndDateChange,
    isEndDateInvalid,
    disableDateFields,
    filterType,
    setFilterType,
    setClients,
    resetClients,
    mapReportTypeToClientSource,
    selectedTpas,
    setSelectedTpas,
    handleTpaSelection,
    showWeekDropdown,
    availableWeeks,
    selectedWeek,
    handleWeekChange,
    selectedMonth,
    setSelectedMonth,
    months,
    selectedYear,
    handleYearChange,
    monthYearDisabled,
    handleGenerateReport,
    isButtonDisabled,
    reportTypes,
    isMedSubReport,
    setIsMedSubReport,
    latestClaimDate,
    setOpenMasterDialog,
  } = useReportForm();

  const DYNAMIC_CLIENT_REPORTS = [
    REPORT_TYPES.MEMBER_SUMMARY,
    REPORT_TYPES.PA_STATUS_REPORT,
    REPORT_TYPES.INVOICE_DETAIL,
  ];

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <FormControl fullWidth sx={{ width: 200, flexGrow: 1 }}>
          <InputLabel>{FORM_LABELS.REPORT_TYPE}</InputLabel>
          <Select value={reportType} onChange={handleReportTypeChange}>
            {reportTypes.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label={FORM_LABELS.START_DATE}
          type="date"
          value={startDate}
          onChange={handleStartDateChange}
          error={isStartDateInvalid}
          InputLabelProps={{ shrink: true }}
          fullWidth
          sx={{ width: 200 }}
          disabled={disableDateFields || isMedSubReport}
        />
        <TextField
          label={FORM_LABELS.END_DATE}
          type="date"
          value={endDate}
          onChange={handleEndDateChange}
          error={isEndDateInvalid}
          InputLabelProps={{ shrink: true }}
          fullWidth
          sx={{ width: 200 }}
          disabled={disableDateFields || isMedSubReport}
        />

        {reportType === REPORT_TYPES.MEMBER_SUMMARY || reportType === REPORT_TYPES.PA_STATUS_REPORT ? (
          <>
            <FormControl component="fieldset" sx={{ width: '18%', mb: '-9px' }}>
              <RadioGroup
                row
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                sx={{ flexWrap: 'nowrap' }}
              >
                <FormControlLabel
                  value="client"
                  control={<Radio size="small" />}
                  label={FORM_LABELS.FILTER_BY_CLIENT}
                />
                <FormControlLabel value="tpa" control={<Radio size="small" />} label={FORM_LABELS.FILTER_BY_TPA} />
              </RadioGroup>
            </FormControl>
            <Box sx={{ width: 400, flexGrow: 1 }}>
              {filterType === 'client' ? (
                <ClientList
                  setFunction={setClients}
                  reset={resetClients}
                  report_type={mapReportTypeToClientSource(reportType)}
                  filter={reportType ? { report_type: mapReportTypeToClientSource(reportType) } : {}}
                />
              ) : reportType === REPORT_TYPES.MEMBER_SUMMARY || reportType === REPORT_TYPES.PA_STATUS_REPORT ? (
                <TpaMultiSelectFilter
                  selectedTpas={selectedTpas}
                  setSelectedTpas={setSelectedTpas}
                  label="Select TPAs"
                  reportType={reportType}
                />
              ) : (
                <TpaList setFunction={handleTpaSelection} reset={resetClients} value={selectedTpas} />
              )}
            </Box>
          </>
        ) : (
          <Box sx={{ width: 400, flexGrow: 1 }}>
            <ClientList
              setFunction={setClients}
              reset={resetClients}
              report_type={DYNAMIC_CLIENT_REPORTS.includes(reportType) ? reportType : 'STANDARD'}
              filter={{
                report_type: DYNAMIC_CLIENT_REPORTS.includes(reportType) ? reportType : 'STANDARD',
              }}
            />
          </Box>
        )}
      </Box>

      {showWeekDropdown && (
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            mb: 2,
            opacity: isMedSubReport ? 0.5 : 1,
            pointerEvents: isMedSubReport ? 'none' : 'auto',
          }}
        >
          {!(
            reportType === REPORT_TYPES.PA_REVIEW ||
            reportType === REPORT_TYPES.PA_CLIENT_BILL ||
            reportType === REPORT_TYPES.ASSIST_REVIEW ||
            reportType === REPORT_TYPES.ASSIST_CLIENT_BILL
          ) && (
            <FormControl
              disabled={
                !reportType ||
                ![
                  REPORT_TYPES.FULL_CLAIMS,
                  REPORT_TYPES.GP_SALES_ORDER,
                  REPORT_TYPES.INVOICE_DETAIL,
                  REPORT_TYPES.POS_REBATE,
                  REPORT_TYPES.MEMBER_SUMMARY,
                  REPORT_TYPES.PA_STATUS_REPORT,
                ].includes(reportType)
              }
              fullWidth
              sx={{ mb: 2, width: 200, flexGrow: 1 }}
            >
              <InputLabel>{FORM_LABELS.SELECT_WEEK}</InputLabel>
              <Select value={selectedWeek} onChange={handleWeekChange}>
                {availableWeeks.map((week) => (
                  <MenuItem key={week} value={week}>
                    {week}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl
            disabled={
              !reportType ||
              ![
                REPORT_TYPES.FULL_CLAIMS,
                REPORT_TYPES.GP_SALES_ORDER,
                REPORT_TYPES.INVOICE_DETAIL,
                REPORT_TYPES.POS_REBATE,
                REPORT_TYPES.PA_REVIEW,
                REPORT_TYPES.PA_CLIENT_BILL,
                REPORT_TYPES.ASSIST_REVIEW,
                REPORT_TYPES.ASSIST_CLIENT_BILL,
                REPORT_TYPES.MEMBER_SUMMARY,
                REPORT_TYPES.PA_STATUS_REPORT,
              ].includes(reportType) ||
              monthYearDisabled
            }
            fullWidth
            sx={{ mb: 2, width: 200, flexGrow: 1 }}
          >
            <InputLabel>{FORM_LABELS.SELECT_MONTH}</InputLabel>
            <Select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
              {months.map((month) => (
                <MenuItem key={month} value={month}>
                  {month}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            disabled={
              !reportType ||
              ![
                REPORT_TYPES.FULL_CLAIMS,
                REPORT_TYPES.GP_SALES_ORDER,
                REPORT_TYPES.INVOICE_DETAIL,
                REPORT_TYPES.POS_REBATE,
                REPORT_TYPES.PA_REVIEW,
                REPORT_TYPES.PA_CLIENT_BILL,
                REPORT_TYPES.ASSIST_REVIEW,
                REPORT_TYPES.ASSIST_CLIENT_BILL,
                REPORT_TYPES.MEMBER_SUMMARY,
                REPORT_TYPES.PA_STATUS_REPORT,
              ].includes(reportType) ||
              monthYearDisabled
            }
            fullWidth
            sx={{ mb: 2, width: 200, flexGrow: 1 }}
          >
            <InputLabel>{FORM_LABELS.SELECT_YEAR}</InputLabel>
            <Select value={selectedYear.toString()} onChange={handleYearChange}>
              {Array.from({ length: 15 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                <MenuItem key={year} value={year.toString()}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {(reportType === REPORT_TYPES.PA_REVIEW ||
            reportType === REPORT_TYPES.PA_CLIENT_BILL ||
            reportType === REPORT_TYPES.ASSIST_REVIEW ||
            reportType === REPORT_TYPES.ASSIST_CLIENT_BILL) && <Box sx={{ width: 200, flexGrow: 1, mb: 2 }} />}
        </Box>
      )}
      {latestClaimDate && (
        <Box sx={{ mb: 1, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Latest claim date available: <strong>{latestClaimDate}</strong>
          </Typography>
        </Box>
      )}

      <Box sx={{ mb: 2, textAlign: 'center', display: 'flex', justifyContent: 'center', gap: 2 }}>
        <Button variant="contained" color="primary" onClick={handleGenerateReport} disabled={isButtonDisabled}>
          {BUTTONS.GENERATE_REPORT}
        </Button>
        <Button variant="outlined" color="primary" onClick={() => setOpenMasterDialog(true)} disabled={isMedSubReport}>
          Generate All Standard Reports
        </Button>
      </Box>
    </>
  );
};

export default StandardReportForm;
